"use client";

import React, { useEffect, useRef, useState } from "react";

export interface SubNavItem {
  href: string;
  label: string;
}

interface SubNavBarProps {
  items: SubNavItem[];
  /** id of the section that must scroll out of view before the bar appears (usually the hero) */
  revealAfterId: string;
  className?: string;
}

// How far below the viewport top a section's heading should land when
// jumped to — keeps content clear of the fixed header + this bar.
const SCROLL_OFFSET = 140;

export const SubNavBar: React.FC<SubNavBarProps> = ({ items, revealAfterId, className = "" }) => {
  const [visible, setVisible] = useState(false);
  const [activeHref, setActiveHref] = useState(items[0]?.href ?? "");
  const navRef = useRef<HTMLElement>(null);
  const itemRefs = useRef<Record<string, HTMLAnchorElement | null>>({});
  const isClickScrolling = useRef(false);

  useEffect(() => {
    const revealEl = document.getElementById(revealAfterId);
    const sectionEls = items
      .map(item => document.querySelector(item.href))
      .filter((el): el is Element => el !== null);

    const update = () => {
      if (revealEl) {
        setVisible(revealEl.getBoundingClientRect().bottom < 0);
      }

      if (isClickScrolling.current) return;

      let active = sectionEls[0];
      for (const section of sectionEls) {
        if (section.getBoundingClientRect().top <= 150) active = section;
      }
      if (active) setActiveHref(`#${active.id}`);
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealAfterId]);

  // Keep the active tab scrolled into view within the horizontally
  // scrollable strip (relevant on mobile where it can overflow).
  useEffect(() => {
    itemRefs.current[activeHref]?.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
  }, [activeHref]);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    const target = document.querySelector(href);
    if (!target) return;
    e.preventDefault();

    setActiveHref(href);
    isClickScrolling.current = true;
    const top = target.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET;
    window.scrollTo({ top, behavior: "smooth" });
    window.history.pushState(null, "", href);
    window.setTimeout(() => {
      isClickScrolling.current = false;
    }, 700);
  };

  return (
    <nav
      ref={navRef}
      aria-label="Page sections"
      className={`fixed left-0 right-0 top-[68px] md:top-[90px] z-40 bg-[rgba(23,34,79,0.96)] backdrop-blur-md border-b border-white/10 shadow-[0_10px_30px_rgba(17,24,39,0.12)] transition-all duration-200 ${
        visible ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-full pointer-events-none"
      } ${className}`}
    >
      <div className="container mx-auto px-8 xl:px-0">
        <div className="flex gap-1 overflow-x-auto scrollbar-hide">
          {items.map(item => {
            const isActive = activeHref === item.href;
            return (
              <a
                key={item.href}
                ref={el => {
                  itemRefs.current[item.href] = el;
                }}
                href={item.href}
                onClick={e => handleClick(e, item.href)}
                className={`shrink-0 px-3.5 pt-2.5 pb-2 text-[13px] font-normal border-b-2 transition-colors ${
                  isActive
                    ? "text-white border-secondary"
                    : "text-white/70 border-transparent hover:text-white"
                }`}
              >
                {item.label}
              </a>
            );
          })}
        </div>
      </div>
    </nav>
  );
};
